import React, { useRef, memo } from 'react';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';
import { getRandomIconUrl } from 'Shared/helpers';
import { AvatarType } from 'Shared/constants/types';
import { v4 as uuidv4 } from 'uuid';

import './Avatar.scss';

type AvatarProps = {
  avatar?: AvatarType;
  circle?: boolean;
  height: number;
  initialsNotSmallSize?: boolean;
  classNames?: string;
  isGradientBorder?: boolean;
};

function Avatar({
  avatar = null,
  circle = true,
  height,
  initialsNotSmallSize = false,
  classNames = '',
  isGradientBorder = false,
}: AvatarProps) {
  const avatarContainer = useRef(null);
  const outerWrapperColorValue = avatar?.outerWrapperColor || '#EFFFFB';

  const avatarGreenWrapperStyles = {
    height: `${height}px`,
    width: `${height}px`,
    minWidth: `${height}px`,
    backgroundColor: outerWrapperColorValue,
  };
  const avatarWhiteWrapperStyles = { padding: height > 40 ? '2px' : '1px' };

  const backgroundImageStyle = avatar?.profilePictureUrl
    ? {
        backgroundImage: `url("${avatar.profilePictureUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        backgroundColor: avatar?.color,
      };
  return (
    <>
      {avatar && (
        <div
          className={cn(
            `avatar-outer-wrapper d-flex text-white align-items-center justify-content-center ${classNames}`,
            {
              'avatar-circle': circle,
              'avatar-outer-wrapper--is-gradient-border': isGradientBorder,
            }
          )}
          style={avatarGreenWrapperStyles}
          ref={avatarContainer}
        >
          <div
            className={cn('avatar-inner-wrapper bg-white w-100 h-100', { 'avatar-circle': circle })}
            style={avatarWhiteWrapperStyles}
          >
            <div
              className={cn('avatar-image-background d-flex align-items-center justify-content-center w-100 h-100', {
                'avatar-circle': circle,
              })}
              style={backgroundImageStyle}
            >
              <div
                className={cn('d-flex justify-content-center align-items-center w-100 h-100', {
                  initials: !initialsNotSmallSize,
                })}
              >
                {!avatar.profilePictureUrl && avatar.initials}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type PropType = {
  avatar: AvatarType;
  uuid?: string;
  size?: number;
  className?: string;
};

export function SimpleAvatar({ uuid, size = 45, avatar, className }: PropType) {
  if (!avatar)
    return (
      <div style={{ width: size, height: size }} className={`simple-avatar-icon ${className}`}>
        <img src={getRandomIconUrl(uuid || uuidv4(), 'cake')} width="30" alt="" />
      </div>
    );
  const { profilePictureUrl, color, initials } = avatar;
  if (profilePictureUrl)
    return (
      <div style={{ width: size, height: size }} className={`simple-avatar-icon ${className}`}>
        <img src={profilePictureUrl} width="100%" alt="" />
      </div>
    );
  return (
    <div style={{ background: color, width: size, height: size }} className={`simple-avatar ${className}`}>
      {initials}
    </div>
  );
}

export default memo(Avatar, isEqual);
